import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../api";
import { useAuth } from "../AuthContext";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";
import QrScanner from "../components/QrScanner";

export default function RentalDetail() {
  const { rentalId } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [lastAddedId, setLastAddedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesReminder, setNotesReminder] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getRental(rentalId);
      setRental(data);
    } finally {
      setLoading(false);
    }
  }, [rentalId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Highlight nach 3s wieder entfernen, sonst flackert's bei Re-Render erneut
  useEffect(() => {
    if (!lastAddedId) return;
    const t = setTimeout(() => setLastAddedId(null), 3000);
    return () => clearTimeout(t);
  }, [lastAddedId]);

  const onReturn = async (reId) => {
    setReturning(reId);
    // Vor dem Return: aktive Items zählen
    const activeBefore = (rental?.equipment_associations || []).filter((a) => !a.returned_at).length;
    try {
      await api.returnItem(reId);
      const updated = await api.getRental(rentalId);
      setRental(updated);
      // Reminder-Modal wenn letzter Return + Notiz vorhanden
      const activeAfter = (updated.equipment_associations || []).filter((a) => !a.returned_at).length;
      const justClosed = activeBefore > 0 && activeAfter === 0;
      if (justClosed && updated.notes && updated.notes.trim()) {
        setNotesReminder(updated.notes.trim());
      }
    } finally {
      setReturning(null);
    }
  };

  const startEditNotes = () => {
    setNotesDraft(rental?.notes || "");
    setEditingNotes(true);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const updated = await api.updateRental(rentalId, { notes: notesDraft.trim() || null });
      setRental(updated);
      setEditingNotes(false);
    } finally {
      setSavingNotes(false);
    }
  };

  const onDelete = async () => {
    await api.deleteRental(rentalId);
    navigate("/rentals");
  };

  if (loading) return <div className="text-fh-gray">Lade…</div>;
  if (!rental) return <div className="text-fh-gray">Verleih nicht gefunden.</div>;

  const fullAddr = [rental.street, rental.house_number, rental.zip_code, rental.city, rental.country]
    .filter(Boolean).join(" ");
  const activeAssocs = rental.equipment_associations?.filter((a) => !a.returned_at) || [];
  const returnedAssocs = rental.equipment_associations?.filter((a) => a.returned_at) || [];

  return (
    <div className="space-y-4">
      <Link to="/rentals" className="text-sm text-fh-gray hover:text-fh-black">← Zurück zur Liste</Link>

      <div className="card p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl">{rental.first_name} {rental.last_name}</h1>
              <StatusBadge status={rental.status} />
            </div>
            <div className="text-sm text-fh-gray mt-1 font-mono">{rental.rental_id}</div>
          </div>
          {isAdmin && (
            <button onClick={() => setConfirmDelete(true)} className="btn-secondary text-fh-red">
              Verleih löschen
            </button>
          )}
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Email" value={rental.customer_email} />
          <Field label="Telefon" value={rental.phone || "—"} />
          {fullAddr && <Field label="Adresse" value={fullAddr} />}
          <Field label="Standort" value={rental.location?.name || "—"} />
          <Field label="Erstellt am" value={new Date(rental.start_date).toLocaleString("de-DE")} />
          {rental.return_date && (
            <Field label="Zurück am" value={new Date(rental.return_date).toLocaleString("de-DE")} />
          )}
        </div>
      </div>

      {/* Schicht-Notiz */}
      <NotesCard
        rental={rental}
        editing={editingNotes}
        draft={notesDraft}
        setDraft={setNotesDraft}
        onStart={startEditNotes}
        onCancel={() => setEditingNotes(false)}
        onSave={saveNotes}
        saving={savingNotes}
      />

      {/* Items zuweisen */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-semibold">Aktiv verliehene Items ({activeAssocs.length})</h2>
            {returnedAssocs.length > 0 && (
              <p className="text-xs text-fh-gray mt-0.5">{returnedAssocs.length} bereits zurück</p>
            )}
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className={showAdd ? "btn-secondary" : "btn-primary"}
          >
            {showAdd ? "Schließen" : "➕ Items zuweisen"}
          </button>
        </div>

        {/* Items-Liste — beim Zuweisen oben, sonst klassisch unten */}
        <ItemsList
          rental={rental}
          returning={returning}
          onReturn={onReturn}
          lastAddedId={lastAddedId}
          showAdd={showAdd}
        />

        {showAdd && (
          <AddItemsPanel
            rentalId={rental.rental_id}
            onAdded={async (addedItemId) => {
              if (addedItemId) setLastAddedId(addedItemId);
              await reload();
            }}
          />
        )}
      </div>

      <Modal
        open={!!notesReminder}
        title="📌 Notiz beachten"
        onClose={() => setNotesReminder(null)}
        size="md"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setNotesReminder(null)}
              disabled={savingNotes}
            >
              📌 Bleibt offen
            </button>
            <button
              className="btn-primary"
              onClick={async () => {
                setSavingNotes(true);
                try {
                  const updated = await api.updateRental(rentalId, { notes: null });
                  setRental(updated);
                } finally {
                  setSavingNotes(false);
                  setNotesReminder(null);
                }
              }}
              disabled={savingNotes}
            >
              {savingNotes ? "…" : "✓ Erledigt — Notiz löschen"}
            </button>
          </>
        }
      >
        <p className="text-sm text-fh-gray mb-3">
          Alle Items sind zurück — vor dem Abschluss bitte folgende Notiz beachten:
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 px-4 py-3 rounded text-fh-black whitespace-pre-wrap">
          {notesReminder}
        </div>
        <p className="text-xs text-fh-gray mt-3">
          Wähle <strong>„Erledigt"</strong> wenn die Notiz abgehandelt ist (z.B. Helm wurde gerade zurückgegeben).
          Wähle <strong>„Bleibt offen"</strong> wenn der Punkt noch offen ist (z.B. Anzahlung steht noch aus) — die Notiz bleibt am Verleih sichtbar.
        </p>
      </Modal>

      <Modal
        open={confirmDelete}
        title="Verleih wirklich löschen?"
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setConfirmDelete(false)}>Abbrechen</button>
            <button className="btn-danger" onClick={onDelete}>Löschen</button>
          </>
        }
      >
        <p className="text-sm text-fh-gray">
          Der gesamte Verleih inkl. aller Item-Zuordnungen wird gelöscht.
          Aktuell verliehene Items werden wieder als verfügbar markiert.
        </p>
      </Modal>
    </div>
  );
}

function NotesCard({ rental, editing, draft, setDraft, onStart, onCancel, onSave, saving }) {
  const hasNotes = rental?.notes && rental.notes.trim();

  if (editing) {
    return (
      <div className="card p-4 border-l-4 border-yellow-400">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-yellow-600 text-lg">📌</span>
          <h3 className="font-semibold">Schicht-Notiz</h3>
        </div>
        <textarea
          className="input-field"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="z.B. Helm noch zurückgeben, Anzahlung offen, Kunde kommt nochmal vorbei …"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-3">
          <button className="btn-secondary" onClick={onCancel} disabled={saving}>Abbrechen</button>
          <button className="btn-primary" onClick={onSave} disabled={saving}>
            {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    );
  }

  if (!hasNotes) {
    return (
      <button
        onClick={onStart}
        className="card p-3 w-full text-left text-sm text-fh-gray hover:text-fh-black hover:bg-fh-gray-light transition-colors flex items-center gap-2"
      >
        <span>📌</span>
        <span>Schicht-Notiz hinzufügen…</span>
      </button>
    );
  }

  return (
    <div className="card p-4 border-l-4 border-yellow-400 bg-yellow-50/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <span className="text-yellow-600 text-lg leading-none mt-0.5">📌</span>
          <div className="flex-1">
            <h3 className="font-semibold text-sm uppercase tracking-wider text-fh-gray mb-1">Schicht-Notiz</h3>
            <p className="text-fh-black whitespace-pre-wrap">{rental.notes}</p>
          </div>
        </div>
        <button onClick={onStart} className="btn-ghost text-sm flex-shrink-0">Bearbeiten</button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fh-gray font-semibold">{label}</div>
      <div className="mt-0.5 break-words">{value}</div>
    </div>
  );
}

function ItemsList({ rental, returning, onReturn, lastAddedId, showAdd }) {
  const assocs = rental.equipment_associations || [];

  // Sortierung: zuletzt ausgegebenes Item nach OBEN, damit es direkt sichtbar ist
  const sorted = [...assocs].sort((a, b) => {
    const ta = a.rented_at ? new Date(a.rented_at).getTime() : 0;
    const tb = b.rented_at ? new Date(b.rented_at).getTime() : 0;
    return tb - ta;
  });

  if (assocs.length === 0) {
    return (
      <div className="p-8 text-center text-fh-gray">
        {showAdd
          ? "Noch keine Items zugewiesen — scanne unten den ersten QR-Code."
          : 'Noch keine Items zugewiesen. Klick „Items zuweisen", um zu beginnen.'}
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {sorted.map((a) => {
        const eq = a.item?.equipment;
        const itemLabel = eq
          ? [eq.brand, eq.model, eq.size].filter(Boolean).join(" ")
          : a.item_id;
        const isLastAdded = a.item_id === lastAddedId;
        return (
          <li
            key={a.id}
            className={`px-5 py-3 flex items-center gap-3 flex-wrap ${isLastAdded ? "row-pulse" : ""}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isLastAdded && <span className="text-green-600 text-lg leading-none">✓</span>}
                <Link to={`/items/${a.item_id}`} className="font-mono font-medium hover:underline">
                  {a.item_id}
                </Link>
                <span className="text-fh-gray text-sm">{itemLabel}</span>
                {a.returned_at && <span className="badge badge-available">zurück</span>}
              </div>
              <div className="text-xs text-fh-gray mt-0.5">
                {a.rented_at && `Ausgegeben: ${new Date(a.rented_at).toLocaleString("de-DE")}`}
                {a.rented_by_user_id && ` · von ${a.rented_by_user_id}`}
                {a.returned_at && ` · Zurück: ${new Date(a.returned_at).toLocaleString("de-DE")}`}
                {a.returned_by_user_id && ` · ${a.returned_by_user_id}`}
              </div>
              {a.comment && <div className="text-xs text-fh-gray italic mt-0.5">„{a.comment}"</div>}
            </div>
            {!a.returned_at && (
              <button
                onClick={() => onReturn(a.id)}
                disabled={returning === a.id}
                className="btn-primary text-sm"
              >
                {returning === a.id ? "…" : "Zurück annehmen"}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function AddItemsPanel({ rentalId, onAdded }) {
  const [mode, setMode] = useState("scan"); // scan | search
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [search, setSearch] = useState("");
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (mode !== "search") return;
    api.listItems({ status: "available" }).then(setAllItems).catch(() => {});
  }, [mode]);

  const flashOk = (text) => {
    setFlash({ kind: "ok", text });
    setTimeout(() => setFlash(null), 1400);
  };
  const flashWarn = (text) => {
    setFlash({ kind: "warn", text });
    setTimeout(() => setFlash(null), 1800);
  };

  const addById = async (itemId) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.addItemsToRental(rentalId, { item_ids: [itemId], comment: comment || null });
      await onAdded(itemId);
      flashOk(`+ ${itemId} zugewiesen`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Fehler beim Zuweisen";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onScan = async (decoded) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = extractToken(decoded);
      const item = await api.scanItem(token);
      if (item.status !== "available") {
        flashWarn(`${item.id} ist ${item.status}`);
        return;
      }
      await api.addItemsToRental(rentalId, { item_ids: [item.id], comment: comment || null });
      await onAdded(item.id);
      flashOk(`+ ${item.id} zugewiesen`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Unbekannter Code";
      setError(msg);
    } finally {
      // Kurze Pause, damit derselbe Frame nicht doppelt addiert
      setTimeout(() => setBusy(false), 800);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems.slice(0, 50);
    return allItems
      .filter((i) => {
        const eq = i.equipment;
        return [i.id, eq?.brand, eq?.model, eq?.size, eq?.category]
          .filter(Boolean).join(" ").toLowerCase().includes(q);
      })
      .slice(0, 50);
  }, [allItems, search]);

  return (
    <div className="bg-fh-gray-light p-5 border-b border-gray-200">
      <div className="flex border-b border-gray-200 mb-3 -mx-5 -mt-5">
        {[
          { v: "scan", l: "📷 QR scannen" },
          { v: "search", l: "🔍 Suchen" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setMode(opt.v)}
            className={[
              "flex-1 px-4 py-3 text-sm font-medium transition-colors",
              mode === opt.v ? "bg-white border-b-2 border-fh-red text-fh-black" : "text-fh-gray hover:bg-white/50",
            ].join(" ")}
          >
            {opt.l}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          {mode === "scan" ? (
            <QrScanner onScan={onScan} paused={busy} onError={() => setError("Kamera nicht verfügbar")} />
          ) : (
            <div className="space-y-2">
              <input
                className="input-field"
                type="search"
                placeholder="Item-ID, Marke, Modell, Größe…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded">
                {filtered.length === 0 ? (
                  <li className="p-4 text-sm text-fh-gray text-center">Keine verfügbaren Items.</li>
                ) : filtered.map((i) => {
                  const eq = i.equipment;
                  return (
                    <li key={i.id} className="px-3 py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono text-sm">{i.id}</div>
                        <div className="text-xs text-fh-gray truncate">
                          {[eq?.brand, eq?.model, eq?.size].filter(Boolean).join(" ") || eq?.category}
                        </div>
                      </div>
                      <button onClick={() => addById(i.id)} className="btn-primary text-sm" disabled={busy}>+ Zuweisen</button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-field">Notiz (optional)</label>
            <textarea
              className="input-field"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="z.B. Größe getestet, Zustand …"
            />
          </div>
          {flash && (
            <div className={`text-sm font-medium px-3 py-2 rounded ${flash.kind === "ok" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-800"}`}>
              {flash.text}
            </div>
          )}
          {error && (
            <div className="bg-fh-red/10 text-fh-red-dark text-sm px-3 py-2 rounded">{error}</div>
          )}
          <p className="text-xs text-fh-gray">
            Item-Status wird automatisch auf „verliehen" gesetzt und beim Zurück-annehmen wieder freigegeben.
          </p>
        </div>
      </div>
    </div>
  );
}

function extractToken(decoded) {
  try {
    const url = new URL(decoded);
    const parts = url.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || decoded;
  } catch {
    return decoded;
  }
}
